import { z } from 'zod';
import { UserSummary } from './admin';
import { BooleanQuery, Id, IsoDate } from './common';

/**
 * Termes d'exclusion (`data-model.md` § `exclusion_terms`, `api.md` § Configuration et
 * gouvernance, fiches T16 et T28). Les énumérations sont ré-exportées par `core`
 * (`ExclusionCategorySchema`, `ExclusionMatchSchema`).
 */

/** Catégories de la table `exclusion_terms` (`custom` porte les termes « soft »). */
export const EXCLUSION_CATEGORY_VALUES = [
  'adult',
  'gambling',
  'health',
  'finance',
  'legal',
  'custom',
] as const;
export const ExclusionCategory = z.enum(EXCLUSION_CATEGORY_VALUES);
export type ExclusionCategory = z.infer<typeof ExclusionCategory>;

export const EXCLUSION_MATCH_VALUES = ['word', 'contains', 'regex'] as const;
export const ExclusionMatch = z.enum(EXCLUSION_MATCH_VALUES);
export type ExclusionMatch = z.infer<typeof ExclusionMatch>;

export const EXCLUSION_TERM_MAX_LENGTH = 120;

/** `null` si le motif compile, sinon la clé i18n de l'erreur. */
export function regexIssue(pattern: string): string | null {
  try {
    new RegExp(pattern, 'i');
    return null;
  } catch {
    return 'validation.regex';
  }
}

/**
 * Terme tel que stocké : `trim`, minuscule pour `word` et `contains` (la casse d'un motif `regex`
 * est significative : `\S` n'est pas `\s` ; le motif est de toute façon appliqué en `i`).
 */
export function normalizeExclusionTerm(term: string, match: ExclusionMatch): string {
  const trimmed = term.trim();
  return match === 'regex' ? trimmed : trimmed.toLowerCase();
}

export const ExclusionTermRow = z.object({
  id: Id,
  category: ExclusionCategory,
  /** `null` = tous marchés. */
  marketId: z.string().nullable(),
  term: z.string(),
  match: ExclusionMatch,
  isSoft: z.boolean(),
  isActive: z.boolean(),
  /** `null` = seed initial. */
  createdBy: UserSummary.nullable(),
  createdAt: IsoDate,
});
export type ExclusionTermRow = z.infer<typeof ExclusionTermRow>;

export const ExclusionsResponse = z.object({ items: z.array(ExclusionTermRow) });
export type ExclusionsResponse = z.infer<typeof ExclusionsResponse>;

/** `GET /exclusions` : `marketId` renvoie les termes du marché et ceux « tous marchés ». */
export const ExclusionsQuery = z.object({
  category: ExclusionCategory.optional(),
  marketId: z.string().min(1).optional(),
  isActive: BooleanQuery.optional(),
});
export type ExclusionsQuery = z.infer<typeof ExclusionsQuery>;

/** `POST /exclusions` (admin). Un motif `regex` invalide est refusé (`validation.regex`). */
export const CreateExclusionBody = z
  .object({
    category: ExclusionCategory,
    marketId: z.string().min(1).nullable().default(null),
    term: z
      .string()
      .trim()
      .min(1, 'validation.required')
      .max(EXCLUSION_TERM_MAX_LENGTH, 'validation.maxLength'),
    match: ExclusionMatch.default('word'),
    isSoft: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (value.match !== 'regex') return;
    const issue = regexIssue(value.term);
    if (issue) ctx.addIssue({ code: 'custom', path: ['term'], message: issue });
  });
export type CreateExclusionBody = z.infer<typeof CreateExclusionBody>;

/**
 * `DELETE /exclusions/:id` : un terme du seed initial (`createdBy` null) est désactivé
 * (`deactivated`), un terme ajouté par un admin est supprimé (`deleted`).
 */
export const DeleteExclusionResponse = z.object({
  id: Id,
  mode: z.enum(['deleted', 'deactivated']),
});
export type DeleteExclusionResponse = z.infer<typeof DeleteExclusionResponse>;
